import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Tag, message, Popconfirm, Space, Typography, Empty, Segmented } from 'antd';
import { CheckCircleOutlined, StopOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import {
  getPartnerCapabilities, verifyCapability, suspendCapability, type PartnerCapability,
} from '../../services/partnerCapabilityService';

const { Title, Paragraph } = Typography;

const humanize = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const STATUS_META: Record<string, { color: string; label: string }> = {
  pending: { color: 'gold', label: 'Pending' },
  verified: { color: 'green', label: 'Verified' },
  suspended: { color: 'red', label: 'Suspended' },
};

type Filter = 'all' | 'pending' | 'verified' | 'suspended';

const PartnerCapabilitiesPage: React.FC = () => {
  const [rows, setRows] = useState<PartnerCapability[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  const load = async () => {
    setLoading(true);
    try {
      setRows(await getPartnerCapabilities());
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load partner capabilities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const shown = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );

  const keyOf = (r: PartnerCapability) => `${r.partner_id}:${r.capability}`;

  const act = async (r: PartnerCapability, to: 'verify' | 'suspend') => {
    setActing(keyOf(r));
    try {
      if (to === 'verify') await verifyCapability(r.partner_id, r.capability);
      else await suspendCapability(r.partner_id, r.capability);
      message.success(to === 'verify' ? 'Capability verified.' : 'Capability suspended.');
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || `Could not ${to} the capability`);
    } finally {
      setActing(null);
    }
  };

  const columns = [
    {
      title: 'Partner',
      key: 'partner',
      render: (_: unknown, r: PartnerCapability) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.partner_name || 'Partner'}</div>
          <div style={{ color: '#888', fontSize: 12 }}>{r.partner_id.replace(/-/g, '').slice(0, 8)}</div>
        </div>
      ),
    },
    { title: 'Capability', key: 'capability', render: (_: unknown, r: PartnerCapability) => <Tag>{humanize(r.capability)}</Tag> },
    {
      title: 'Countries',
      key: 'countries',
      render: (_: unknown, r: PartnerCapability) =>
        (r.countries ?? []).length ? (r.countries).map((c) => <Tag key={c}>{c}</Tag>) : <span style={{ color: '#aaa' }}>—</span>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_: unknown, r: PartnerCapability) => {
        const m = STATUS_META[r.status] || { color: 'default', label: humanize(r.status) };
        return <Tag color={m.color}>{m.label}</Tag>;
      },
    },
    {
      title: 'Verified',
      key: 'verified_at',
      render: (_: unknown, r: PartnerCapability) => (r.verified_at ? fmtDate(r.verified_at) : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, r: PartnerCapability) => (
        <Space>
          {r.status !== 'verified' && (
            <Popconfirm title="Verify this capability?" onConfirm={() => act(r, 'verify')} okText="Verify">
              <Button type="primary" size="small" icon={<CheckCircleOutlined />} loading={acting === keyOf(r)}>Verify</Button>
            </Popconfirm>
          )}
          {r.status !== 'suspended' && (
            <Popconfirm title="Suspend this capability? Products relying on it should be reviewed." onConfirm={() => act(r, 'suspend')} okText="Suspend" okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<StopOutlined />} loading={acting === keyOf(r)}>Suspend</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>
            <SafetyCertificateOutlined /> Partner Capabilities
          </Title>
          <Paragraph type="secondary">
            Verify the regulated functions each partner is cleared to perform (lending, insurance,
            payroll disbursement, …). Verifying or suspending a capability is recorded in the audit
            trail. Suspending it should trigger a review of any products relying on it.
          </Paragraph>
        </div>
        <Space>
          <Segmented
            value={filter}
            onChange={(v) => setFilter(v as Filter)}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Pending', value: 'pending' },
              { label: 'Verified', value: 'verified' },
              { label: 'Suspended', value: 'suspended' },
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
        </Space>
      </div>
      <Table
        rowKey={keyOf}
        loading={loading}
        columns={columns}
        dataSource={shown}
        pagination={false}
        locale={{ emptyText: <Empty description="No partner capabilities" /> }}
      />
    </div>
  );
};

export default PartnerCapabilitiesPage;
